/**
 * Dreamdata bootstrap: pipeline is chosen once per page load from stored consent.
 *
 * - No stored consent → cookieless (pre-accept only).
 * - Stored consent → standard only (no cookieless inject or events).
 * - Mid-session revoke tears down standard; cookieless is not reinjected (preserves UX/state,
 *   avoids vendor-flag hacks). Returning consented users who revoke may send nothing until reload.
 * - Mid-session re-grant reloads standard via load()+page() when init() is a no-op.
 */
import { COOKIELESS_SNIPPET, STANDARD_SNIPPET, WRITE_KEY } from 'client/GrowthEngineering/Dreamdata/DreamdataConfig';
import { registerDreamdataGlobals } from 'client/GrowthEngineering/Dreamdata/index';
import dreamdataConsent from 'client/GrowthEngineering/Dreamdata/dreamdataConsent';
import optanonWrapperRegistry from 'client/util/oneTrust/optanonWrapperRegistry';

const STANDARD_SCRIPT_ID = 'dreamdata-signup-init';
const COOKIELESS_SCRIPT_ID = 'dreamdata-signup-init-cl';
const LIVE_STANDARD_SCRIPT_ID = 'dreamdata-analytics';

// Must match the options embedded in STANDARD_SNIPPET's dreamdata.init() call.
const STANDARD_LOAD_OPTIONS = {
  googleEnhancedConversions: true,
  formTracking: false,
  intentSources: { autoGroup: true },
};

function injectScript(id, body) {
  if (document.getElementById(id)) return;
  const script = document.createElement('script');
  script.id = id;
  script.textContent = body;
  document.head.appendChild(script);
}

function injectCookieless() {
  injectScript(COOKIELESS_SCRIPT_ID, COOKIELESS_SNIPPET);
}

function activateStandard() {
  if (window.dreamdataInitialized) {
    return;
  }

  // Reset any prior declined/timeout state so grant (and re-grant after revoke) can queue again.
  if (window.DreamdataUiEvents && typeof window.DreamdataUiEvents.markConsentGranted === 'function') {
    window.DreamdataUiEvents.markConsentGranted();
  }

  injectScript(STANDARD_SCRIPT_ID, STANDARD_SNIPPET);

  try {
    if (window.dreamdata && typeof window.dreamdata.init === 'function') {
      window.dreamdata.init();
    }

    // init() is a no-op after the first successful run (vendor closure flag). After revoke we
    // remove #dreamdata-analytics; re-grant must reload via load()+page() without re-injecting
    // the snippet or mutating vendor initialized flags.
    if (
      !document.getElementById(LIVE_STANDARD_SCRIPT_ID)
      && window.dreamdata
      && typeof window.dreamdata.load === 'function'
    ) {
      window.dreamdata.load(WRITE_KEY, STANDARD_LOAD_OPTIONS);
      if (typeof window.dreamdata.page === 'function') {
        window.dreamdata.page();
      }
    }
  } catch (e) {
    // Swallow - Dreamdata must never break signup.
  }

  window.dreamdataInitialized = true;

  if (window.DreamdataEvents && typeof window.DreamdataEvents.init === 'function') {
    window.DreamdataEvents.init();
  }
}

function deactivateStandard() {
  // Only mark the queue declined when standard was actually active. "Not yet consented" must keep
  // the queue open so a later grant can still flush (or the ready-timeout can expire cleanly).
  const wasInitialized = !!window.dreamdataInitialized;

  if (
    wasInitialized
    && window.DreamdataUiEvents
    && typeof window.DreamdataUiEvents.markConsentDeclined === 'function'
  ) {
    window.DreamdataUiEvents.markConsentDeclined();
  }

  const liveScript = document.getElementById(LIVE_STANDARD_SCRIPT_ID);
  if (liveScript) {
    liveScript.remove();
  }

  window.dreamdataInitialized = false;
}

/**
 * Reacts to OneTrust's OptanonWrapper callback via window.OnetrustActiveGroups.
 */
function handleConsentChange() {
  try {
    const activeGroups = window.OnetrustActiveGroups;
    const isConsented = !!activeGroups
      && dreamdataConsent.REQUIRED_CONSENT_GROUPS.every((group) => activeGroups.includes(group));

    if (isConsented) {
      activateStandard();
    } else {
      deactivateStandard();
    }
  } catch (e) {
    // Swallow - Dreamdata must never break signup.
  }
}

function initializeDreamdataBootstrap() {
  try {
    registerDreamdataGlobals();

    const hasStoredConsent = dreamdataConsent.hasDreamdataConsent();

    // Pre-accept only. Do not inject once stored consent exists (no cookieless after accept).
    if (!hasStoredConsent) {
      injectCookieless();
    }

    optanonWrapperRegistry.registerOptanonWrapperCallback(handleConsentChange);

    // Re-read consent immediately in case OneTrust already ran OptanonWrapper before we registered.
    handleConsentChange();
  } catch (e) {
    // Swallow - Dreamdata must never break signup.
  }
}

export default { initializeDreamdataBootstrap };
